import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { provideStore } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateStore } from '@ngx-translate/core';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { AbstractSecurityStorage, AuthModule } from 'angular-auth-oidc-client';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { Observable } from 'rxjs';
import { appRoutes } from './app.routes';
import { AuthInterceptor } from './app/@core/interceptors/auth.interceptors';
import { ChatbotInterceptor } from './app/@core/interceptors/chatbot.interceptor';
import { EnvironmentService } from './app/@core/services/environment.service';
import { TranslationService } from './app/services/translation.service';
import { OidcStorageService } from './oidc-storege.provider';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f0f4ff',
      100: '#e6eeff',
      200: '#d1ddff',
      300: '#00205B',
      400: '#00205B',
      500: '#00205B',
      600: '#00205B',
      700: '#00205B',
      800: '#00205B',
      900: '#00205B',
      950: '#00205B',
    },
  },
});

export class CustomTranslateLoader implements TranslateLoader {
  constructor(private translationService: TranslationService) { }

  getTranslation(lang: string): Observable<any> {
    return this.translationService.getTranslation(lang);
  }
}

export function createTranslateLoader(translationService: TranslationService) {
  return new CustomTranslateLoader(translationService);
}

let envService = new EnvironmentService();

// Factory function that forces Angular to create the analytics services
// export function initAnalytics(
//   analyticsSessionService: AnalyticsSessionService,
//   analyticsEventsService: AnalyticsEventsService,
// ) {
//   return () => Promise.resolve();
// }

export const appConfig: ApplicationConfig = {
  providers: [
    TranslateStore,
    MessageService,
    // This will ensure the analytics services are instantiated immediately at startup
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initAnalytics,
    //   deps: [AnalyticsSessionService, AnalyticsEventsService],
    //   multi: true,
    // },
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withComponentInputBinding(),
    ),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: localStorage.getItem('selectedLanguage') || 'it',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [TranslationService],
        },
      }),
      AuthModule.forRoot({
        config: [
          {
            configId: 'ias-white-oidc',
            authority: envService.iasAuthorityUrl,
            redirectUrl: window.location.origin + '/callback',
            postLogoutRedirectUri: window.location.origin,
            clientId: envService.iasWhiteClientId,
            scope: 'openid profile email groups offline_access',
            responseType: 'code',
            silentRenew: true,
            useRefreshToken: true,
            renewTimeBeforeTokenExpiresInSeconds: 30,
          },
          {
            configId: 'ias-blue-oidc',
            authority: envService.iasAuthorityUrl,
            redirectUrl: window.location.origin + '/callback',
            postLogoutRedirectUri: window.location.origin,
            clientId: envService.iasBlueClientId,
            scope: 'openid profile email groups offline_access',
            responseType: 'code',
            silentRenew: true,
            useRefreshToken: true,
            renewTimeBeforeTokenExpiresInSeconds: 30,
          },
          {
            configId: 'cognito-oidc',
            authority: `https://cognito-idp.${envService.oauthCognitoRegion}.amazonaws.com/${envService.oauthCognitoUserPoolId}`,
            redirectUrl: window.location.origin + '/callback',
            postLogoutRedirectUri: window.location.origin,
            clientId: envService.oauthCognitoClientId,
            scope: 'openid profile email phone',
            responseType: 'code',
            silentRenew: false,
            useRefreshToken: false,
          }
        ]
      }),
    ),
    { provide: AbstractSecurityStorage, useClass: OidcStorageService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ChatbotInterceptor,
      multi: true,
    },
    provideAnimationsAsync(),
    providePrimeNG({
      translation: {
        dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
        dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
        monthNames: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
        monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
        today: 'Oggi',
        clear: 'Pulisci',
        dateFormat: 'dd/mm/yy',
        firstDayOfWeek: 1,
      },
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },

      },
    }),
    provideStore(),
  ],
};
